<template>
  <Dialog
    :is-open="isOpen"
    title="Privacy & Analytics"
    width="600px"
    @close="$emit('close')"
  >
    <div :class="$style.dialogContent">
      <div :class="$style.scrollableContent">
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">What We Track</h3>
          <p :class="$style.description">
            We use privacy-friendly analytics to understand how our app is used
            and improve it. We <strong>never</strong> collect personal
            information, cookies, or track you across websites.
          </p>

          <div :class="$style.trackingList">
            <div :class="$style.trackingItem">
              <div :class="$style.trackingIcon">
                <ChartIcon :size="20" />
              </div>
              <div :class="$style.trackingContent">
                <h4>Page Views</h4>
                <p>When you visit different parts of the app</p>
              </div>
            </div>

            <div :class="$style.trackingItem">
              <div :class="$style.trackingIcon">
                <CheckCircleIcon :size="20" />
              </div>
              <div :class="$style.trackingContent">
                <h4>Task Actions</h4>
                <p>
                  When you create, start, stop, or complete tasks (no personal
                  task names)
                </p>
              </div>
            </div>

            <div :class="$style.trackingItem">
              <div :class="$style.trackingIcon">
                <TrendingUpIcon :size="20" />
              </div>
              <div :class="$style.trackingContent">
                <h4>App Usage</h4>
                <p>When you view analytics, export data, or manage topics</p>
              </div>
            </div>

            <div :class="$style.trackingItem">
              <div :class="$style.trackingIcon">
                <DeviceIcon :size="20" />
              </div>
              <div :class="$style.trackingContent">
                <h4>Device Info</h4>
                <p>
                  Browser type, screen size, and operating system (anonymized)
                </p>
              </div>
            </div>
          </div>
        </div>

        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">What We Don't Track</h3>
          <div :class="$style.notTrackingList">
            <div :class="$style.notTrackingItem">
              <span :class="$style.checkmark">✓</span>
              <span>Your name, email, or personal information</span>
            </div>
            <div :class="$style.notTrackingItem">
              <span :class="$style.checkmark">✓</span>
              <span>Your IP address (anonymized)</span>
            </div>
            <div :class="$style.notTrackingItem">
              <span :class="$style.checkmark">✓</span>
              <span>Your task names or content</span>
            </div>
            <div :class="$style.notTrackingItem">
              <span :class="$style.checkmark">✓</span>
              <span>Your browsing history on other sites</span>
            </div>
            <div :class="$style.notTrackingItem">
              <span :class="$style.checkmark">✓</span>
              <span>Cookies or persistent tracking</span>
            </div>
          </div>
        </div>

        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Analytics Provider</h3>
          <p :class="$style.description">
            We use <strong>PostHog Analytics</strong>, a privacy-focused
            analytics service that:
          </p>
          <ul :class="$style.providerList">
            <li>Is GDPR compliant and doesn't require consent banners</li>
            <li>Doesn't use cookies or collect personal data</li>
            <li>Is open source and transparent about data collection</li>
            <li>Processes data in the EU with strong privacy laws</li>
          </ul>
        </div>

        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Your Choice</h3>
          <p :class="$style.description">
            Choose whether to allow analytics tracking to help us improve the
            app:
          </p>

          <p :class="$style.note">
            💡 <strong>Note:</strong> You can change this setting anytime in the
            Actions menu. Your choice is saved locally and only affects this
            browser.
          </p>
        </div>
      </div>

      <div :class="$style.stickyFooter">
        <div :class="$style.buttonContainer">
          <button @click="acceptAnalytics" :class="$style.acceptButton">
            Accept Analytics
          </button>
          <button @click="declineAnalytics" :class="$style.declineButton">
            Decline Analytics
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "./Dialog.vue";
import { posthogService } from "../services/posthogService";
import {
  ChartIcon,
  CheckCircleIcon,
  TrendingUpIcon,
  DeviceIcon,
} from "./icons";

defineProps<{
  isOpen: boolean;
}>();

function acceptAnalytics() {
  // User accepts analytics
  posthogService.optIn();
  posthogService.initialize();
  posthogService.trackPrivacyChoice(false);
  // Close the dialog
  emit("close");
}

function declineAnalytics() {
  // User declines analytics
  posthogService.optOut();
  posthogService.trackPrivacyChoice(true);
  // Close the dialog
  emit("close");
}

const emit = defineEmits<{
  (e: "close"): void;
}>();
</script>

<style module>
.dialogContent {
  display: flex;
  flex-direction: column;
  height: 70vh;
  max-height: 70vh;
}

.scrollableContent {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.stickyFooter {
  flex-shrink: 0;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: inherit;
}

.section {
  margin-bottom: 24px;
}

.sectionTitle {
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.description {
  margin: 0 0 16px 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.trackingList {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.trackingItem {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.trackingIcon {
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
}

.trackingContent h4 {
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: white;
}

.trackingContent p {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}

.notTrackingList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notTrackingItem {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.checkmark {
  color: #00b894;
  font-weight: bold;
  font-size: 1rem;
}

.providerList {
  margin: 0;
  padding-left: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.providerList li {
  margin-bottom: 6px;
}

.buttonContainer {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.acceptButton {
  flex: 1;
  background: #00b894;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.acceptButton:hover {
  background: #00a085;
}

.declineButton {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.declineButton:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.note {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.closeButton {
  background: #fcd172;
  color: #1a1a1a;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.closeButton:hover {
  background: #f4c25a;
}

/* Responsive design */
@media (max-width: 768px) {
  .trackingItem {
    padding: 10px;
  }

  .choiceLabel {
    padding: 12px;
  }

  .sectionTitle {
    font-size: 1rem;
  }
}
</style>
